import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  List,
  ListItem,
  ListItemText,
  Alert,
  Snackbar,
  Fab,
} from '@mui/material';
import {
  KeyboardArrowUp as ArrowUpIcon,
  Email as EmailIcon,
  AccountBalanceWallet as WalletIcon,
  Send as SendIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  ContentCopy as CopyIcon,
  Download as DownloadIcon,
  Security as SecurityIcon,
  Lock as LockIcon,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
  Brightness6 as BrightnessToggleIcon,
} from '@mui/icons-material';

interface EnhancedTremChainProps {
  isDarkMode: boolean;
  onThemeToggle: () => void;
}

const EnhancedTremChain: React.FC<EnhancedTremChainProps> = ({ 
  isDarkMode, 
  onThemeToggle 
}) => {
  // State management (preserving original functionality)
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [walletAddress, setWalletAddress] = useState('');
  const [walletPrivateKey, setWalletPrivateKey] = useState('');
  const [walletBalance, setWalletBalance] = useState('0');
  
  // Layout toggle state (missing from original)
  const [layoutMode, setLayoutMode] = useState('desktop'); // desktop or mobile
  const [serviceMode, setServiceMode] = useState('messaging'); // messaging or email
  
  // Messaging state
  const [messageToAddress, setMessageToAddress] = useState('');
  const [messageContent, setMessageContent] = useState('');
  const [messageType, setMessageType] = useState('text');
  const [messages, setMessages] = useState<any[]>([]);
  
  // Email state
  const [emailToAddress, setEmailToAddress] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [emailPriority, setEmailPriority] = useState('normal');
  const [emails, setEmails] = useState<any[]>([]);
  
  // Conversation state (like in original app)
  const [conversations, setConversations] = useState<any[]>([]);
  const [currentConversation, setCurrentConversation] = useState<string>('');
  const [conversationMessages, setConversationMessages] = useState<any[]>([]);
  
  // UI state
  const [notification, setNotification] = useState({ show: false, message: '', type: 'info' });
  const [loadingState, setLoadingState] = useState({ wallet: false, message: false, email: false });

  // PRODUCTION PRIVATE KEY MANAGEMENT STATE
  const [privateKeyVisible, setPrivateKeyVisible] = useState(false);
  const [showPrivateKeySection, setShowPrivateKeySection] = useState(false);

  // PROGRESSIVE THEME STATE (0-6, where 0 is lightest, 6 is darkest)
  const [themeLevel, setThemeLevel] = useState(isDarkMode ? 6 : 0);

  // AUTO-MINING STATE (enabled by default for better UX)
  const [autoMineEnabled, setAutoMineEnabled] = useState(true);
  
  // NO NOTIFICATION STATE - All automatic notifications removed
  const [pendingMessages, setPendingMessages] = useState<any[]>([]);
  const [confirmedMessages, setConfirmedMessages] = useState<any[]>([]);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.pageYOffset > 300);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // EXACT copy from production HTML - wallet creation function
  const createWallet = async () => {
    console.log('Creating wallet...');
    setLoadingState({ ...loadingState, wallet: true });
    
    try {
      showNotification('Creating wallet...', 'info');
      
      // Try direct backend connection first (since proxy is having issues)
      let response;
      try {
        // Direct backend connection (development)
        console.log('Trying direct backend connection...');
        response = await fetch('http://localhost:3000/api/wallet/create', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          }
        });
      } catch (directError) {
        console.log('Direct connection failed, trying proxy:', directError);
        // Fallback to proxy (production)
        const API_BASE = window.location.origin + '/api';
        response = await fetch(`${API_BASE}/wallet/create`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          }
        });
      }
      
      console.log('Response status:', response.status);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const wallet = await response.json();
      console.log('Wallet created:', wallet);
      
      // Set wallet data exactly like production
      setWalletAddress(wallet.address);
      setWalletPrivateKey(wallet.privateKey);
      setWalletBalance('0');
      
      showNotification('Wallet created successfully!', 'success');
      
      // Load balance and conversations like original app
      setTimeout(() => {
        loadBalance();
        loadMessages();
        loadEmails();
        loadConversations();
      }, 500);
      
      // Additional notification like production
      setTimeout(() => {
        showNotification('💡 New wallet created! Don\'t forget to backup your private key for security.', 'info');
      }, 1000);
      
    } catch (error) {
      console.error('Wallet creation error:', error);
      showNotification('Error creating wallet: ' + (error as Error).message, 'error');
    }
    
    setLoadingState({ ...loadingState, wallet: false });
  };

  const sendMessage = async () => {
    if (!messageToAddress || !messageContent || !walletPrivateKey) {
      showNotification('Please fill in all fields and create a wallet first', 'warning');
      return;
    }
    
    setLoadingState({ ...loadingState, message: true });
    try {
      // Use production API pattern
      const API_BASE = window.location.origin + '/api';
      const response = await fetch(`${API_BASE}/tremsbcmsg/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fromPrivateKey: walletPrivateKey,
          toAddress: messageToAddress,
          message: messageContent,
          messageType: messageType
        })
      });
      
      const result = await response.json();
      if (result.success) {
        showNotification('Message sent successfully!', 'success');
        playNotificationSound('sent');
        
        // Store address before clearing
        const sentToAddress = messageToAddress;
        
        setMessageToAddress('');
        setMessageContent('');
        loadMessages();
        loadConversations(); // Refresh conversations after sending
        
        // Immediately update conversation view
        setTimeout(() => {
          loadConversationMessages(sentToAddress);
          setCurrentConversation(sentToAddress);
        }, 500);
        
        // Auto-mine if enabled
        console.log('🔍 Auto-mine check: autoMineEnabled =', autoMineEnabled);
        if (autoMineEnabled) {
          showNotification('✓ Message sent - auto-mining in progress...', 'info');
          console.log('🔄 Starting auto-mining for message...');
          setTimeout(async () => {
            try {
              const miningResult = await mineBlockWithResult();
              if (miningResult.success) {
                showNotification(`🔗 Message confirmed on blockchain! Block #${miningResult.blockNumber}`, 'success');
                
                // CONVERSATION THREADING: Reload messages after mining (NO notifications)
                setTimeout(() => {
                  loadMessages(); // Just reload data, no notifications
                  loadConversations(); // This ensures the message appears in conversation pool for replies
                  // Refresh current conversation if it matches
                  if (currentConversation === sentToAddress) {
                    loadConversationMessages(sentToAddress);
                  }
                }, 2000);
              } else {
                showNotification(`⚠️ Message sent but auto-mining failed: ${miningResult.error}`, 'warning');
              }
            } catch (error) {
              console.error('Auto-mine error:', error);
              showNotification(`⚠️ Message sent but auto-mining failed: ${(error as Error).message}`, 'warning');
            }
          }, 500);
        }
      } else {
        showNotification('Error sending message: ' + result.error, 'error');
      }
    } catch (error) {
      showNotification('Error sending message: ' + (error as Error).message, 'error');
    }
    setLoadingState({ ...loadingState, message: false });
  };

  const sendEmail = async () => {
    if (!emailToAddress || !emailSubject || !emailBody || !walletPrivateKey) {
      showNotification('Please fill in all fields and create a wallet first', 'warning');
      return;
    }
    
    setLoadingState({ ...loadingState, email: true });
    try {
      // Use production API pattern
      const API_BASE = window.location.origin + '/api';
      const response = await fetch(`${API_BASE}/tremsbcmail/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fromPrivateKey: walletPrivateKey,
          toAddress: emailToAddress,
          subject: emailSubject,
          body: emailBody,
          priority: emailPriority
        })
      });
      
      const result = await response.json();
      if (result.success) {
        showNotification('Email sent successfully!', 'success');
        playNotificationSound('sent');
        
        // Store address before clearing
        const sentToAddress = emailToAddress;
        
        setEmailToAddress('');
        setEmailSubject('');
        setEmailBody('');
        loadEmails();
        loadConversations(); // Refresh conversations after sending email
        
        // Immediately update conversation view
        setTimeout(() => {
          loadConversationMessages(sentToAddress);
          setCurrentConversation(sentToAddress);
        }, 500);
        
        // Auto-mine if enabled
        if (autoMineEnabled) {
          showNotification('✓ Email sent - auto-mining in progress...', 'info');
          setTimeout(async () => {
            try {
              const miningResult = await mineBlockWithResult();
              if (miningResult.success) {
                showNotification(`🎉 Email sent and auto-mined successfully! Block #${miningResult.blockNumber} created`, 'success');
                
                // CONVERSATION THREADING: Reload emails after mining (NO notifications)
                setTimeout(() => {
                  loadEmails(); // Just reload data, no notifications
                  loadConversations(); // Refresh conversations to include mined email
                  // Refresh current conversation if it matches
                  if (currentConversation === sentToAddress) {
                    loadConversationMessages(sentToAddress);
                  }
                }, 2000);
              } else {
                showNotification(`⚠️ Email sent but auto-mining failed: ${miningResult.error}`, 'warning');
              }
            } catch (error) {
              console.error('Auto-mine error:', error);
              showNotification(`⚠️ Email sent but auto-mining failed: ${(error as Error).message}`, 'warning');
            }
          }, 500);
        }
      } else {
        showNotification('Error sending email: ' + result.error, 'error');
      }
    } catch (error) {
      showNotification('Error sending email: ' + (error as Error).message, 'error');
    }
    setLoadingState({ ...loadingState, email: false });
  };

  // LOAD MESSAGES - NO NOTIFICATION CHECKING AT ALL
  const loadMessages = async () => {
    if (!walletAddress) return;
    try {
      const API_BASE = window.location.origin + '/api';
      const response = await fetch(`${API_BASE}/tremsbcmsg/${walletAddress}/messages`);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Messages loaded:', data);
        
        // NO NOTIFICATION CHECKING - just load data
        const allMessages = data.messages || [];
        const pending = allMessages.filter((msg: any) => msg.status === 'pending' || !msg.confirmed);
        const confirmed = allMessages.filter((msg: any) => msg.status === 'confirmed' || msg.confirmed);
        
        setPendingMessages(pending);
        setConfirmedMessages(confirmed);
        setMessages(allMessages); // This populates conversation pool for replies
      } else {
        console.error('Failed to load messages:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  };

  // LOAD EMAILS - NO NOTIFICATION CHECKING AT ALL
  const loadEmails = async () => {
    if (!walletAddress) return;
    try {
      const API_BASE = window.location.origin + '/api';
      const response = await fetch(`${API_BASE}/tremsbcmail/${walletAddress}/inbox`);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Emails loaded:', data);
        
        // NO NOTIFICATION CHECKING - just load data
        const allEmails = data.emails || [];
        setEmails(allEmails); // This populates email conversation pool for replies
      } else {
        console.error('Failed to load emails:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Error loading emails:', error);
    }
  };

  // UNIFIED CONVERSATION MANAGEMENT - Messages AND Emails like iMessage/WhatsApp
  const loadConversations = async () => {
    if (!walletAddress) return;
    try {
      const API_BASE = window.location.origin + '/api';
      
      // Load both messages and emails for unified conversations
      const [messagesResponse, emailsResponse] = await Promise.all([
        fetch(`${API_BASE}/tremsbcmsg/${walletAddress}/messages`),
        fetch(`${API_BASE}/tremsbcmail/${walletAddress}/inbox`)
      ]);
      
      const messagesData = messagesResponse.ok ? await messagesResponse.json() : { messages: [] };
      const emailsData = emailsResponse.ok ? await emailsResponse.json() : { emails: [] };
      
      const messages = messagesData.messages || [];
      const emails = emailsData.emails || [];
      
      // Combine messages and emails into unified conversation threads
      const conversationMap = new Map<string, any>();
      
      // Add messages to conversations
      messages.forEach((msg: any) => {
        const otherAddress = msg.from === walletAddress ? msg.to : msg.from;
        const conversationKey = otherAddress;
        
        if (!conversationMap.has(conversationKey)) {
          conversationMap.set(conversationKey, {
            address: otherAddress,
            messages: [],
            emails: [],
            allItems: [],
            lastActivity: msg,
            hasMessages: false,
            hasEmails: false,
            unreadCount: 0
          });
        }
        
        const conversation = conversationMap.get(conversationKey)!;
        conversation.messages.push({ ...msg, type: 'message' });
        conversation.allItems.push({ ...msg, type: 'message', timestamp: msg.timestamp });
        conversation.hasMessages = true;
        
        // Update last activity if this is newer
        if (new Date(msg.timestamp) > new Date(conversation.lastActivity.timestamp)) {
          conversation.lastActivity = { ...msg, type: 'message' };
        }
      });
      
      // Add emails to conversations
      emails.forEach((email: any) => {
        const otherAddress = email.from === walletAddress ? email.to : email.from;
        const conversationKey = otherAddress;
        
        if (!conversationMap.has(conversationKey)) {
          conversationMap.set(conversationKey, {
            address: otherAddress,
            messages: [],
            emails: [],
            allItems: [],
            lastActivity: email,
            hasMessages: false,
            hasEmails: false,
            unreadCount: 0
          });
        }
        
        const conversation = conversationMap.get(conversationKey)!;
        conversation.emails.push({ ...email, type: 'email' });
        conversation.allItems.push({ ...email, type: 'email', timestamp: email.timestamp });
        conversation.hasEmails = true;
        
        // Update last activity if this is newer
        if (new Date(email.timestamp) > new Date(conversation.lastActivity.timestamp)) {
          conversation.lastActivity = { ...email, type: 'email' };
        }
      });
      
      // Sort all items in each conversation by timestamp (like iMessage/WhatsApp)
      conversationMap.forEach((conversation) => {
        conversation.allItems.sort((a: any, b: any) => 
          new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
        );
      });
      
      // Convert to array and sort by last activity time
      const conversationsList = Array.from(conversationMap.values()).sort(
        (a, b) => new Date(b.lastActivity.timestamp).getTime() - new Date(a.lastActivity.timestamp).getTime()
      );
      
      setConversations(conversationsList);
      console.log('Unified conversations loaded (messages + emails):', conversationsList);
    } catch (error) {
      console.error('Error loading unified conversations:', error);
    }
  };

  // LOAD UNIFIED CONVERSATION THREAD - Messages AND Emails like iMessage/WhatsApp
  const loadConversationMessages = async (otherAddress: string) => {
    if (!walletAddress || !otherAddress) return;
    try {
      const API_BASE = window.location.origin + '/api';
      
      // Use the new unified conversation endpoint
      const response = await fetch(`${API_BASE}/unified/${walletAddress}/${otherAddress}/conversation`);
      
      if (response.ok) {
        const data = await response.json();
        const unifiedConversation = data.conversation || [];
        
        setConversationMessages(unifiedConversation);
        setCurrentConversation(otherAddress);
        console.log('Unified conversation thread loaded for', otherAddress, {
          messages: data.messageCount || 0,
          emails: data.emailCount || 0,
          total: unifiedConversation.length,
          items: unifiedConversation
        });
        
        // Debug: Check message content decryption
        unifiedConversation.forEach((item: any, index: number) => {
          if (item.type === 'message') {
            console.log(`Message ${index}:`, {
              from: item.from,
              to: item.to,
              content: item.content,
              contentLength: item.content?.length || 0,
              confirmed: item.confirmed || item.status === 'confirmed'
            });
          }
        });
        
        showNotification(`📱 Loaded ${data.messageCount || 0} messages and ${data.emailCount || 0} emails`, 'success');
      } else {
        console.error('Failed to load unified conversation:', response.status);
      }
    } catch (error) {
      console.error('Error loading unified conversation thread:', error);
    }
  };

  // START NEW CONVERSATION FUNCTION
  const startNewConversation = () => {
    // Clear all fields
    setMessageToAddress('');
    setMessageContent('');
    setEmailToAddress('');
    setEmailSubject('');
    setEmailBody('');
    
    // Clear current conversation
    setCurrentConversation('');
    setConversationMessages([]);
    
    showNotification('💬 Ready to start new conversation', 'info');
  };

  const showNotification = (message: string, type: 'success' | 'error' | 'warning' | 'info') => {
    setNotification({ show: true, message, type });
  };

  // PRODUCTION FUNCTIONS - COPY/BACKUP/RESTORE
  const copyAddressToClipboard = async () => {
    if (!walletAddress) {
      showNotification('No address to copy', 'error');
      return;
    }
    
    try {
      // Modern clipboard API
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(walletAddress);
        showNotification('Address copied to clipboard!', 'success');
      } else {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = walletAddress;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        try {
          document.execCommand('copy');
          showNotification('Address copied to clipboard!', 'success');
        } catch (err) {
          showNotification('Failed to copy address', 'error');
        }
        
        document.body.removeChild(textArea);
      }
    } catch (err) {
      console.error('Failed to copy address:', err);
      showNotification('Failed to copy address', 'error');
    }
  };

  const copyPrivateKey = async () => {
    if (!walletPrivateKey) {
      showNotification('No private key to copy', 'error');
      return;
    }
    
    try {
      // Modern clipboard API
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(walletPrivateKey);
        showNotification('Private key copied to clipboard! Keep it safe!', 'success');
      } else {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = walletPrivateKey;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        try {
          document.execCommand('copy');
          showNotification('Private key copied to clipboard! Keep it safe!', 'success');
        } catch (err) {
          showNotification('Failed to copy private key', 'error');
        }
        
        document.body.removeChild(textArea);
      }
    } catch (err) {
      console.error('Failed to copy private key:', err);
      showNotification('Failed to copy private key', 'error');
    }
  };

  const downloadPrivateKey = () => {
    if (!walletPrivateKey || !walletAddress) {
      showNotification('No private key to download', 'error');
      return;
    }
    
    const walletData = {
      address: walletAddress,
      privateKey: walletPrivateKey,
      createdAt: new Date().toISOString(),
      platform: 'TremChain',
      warning: 'KEEP THIS FILE SECURE AND NEVER SHARE YOUR PRIVATE KEY!'
    };
    
    const dataStr = JSON.stringify(walletData, null, 2);
    const dataBlob = new Blob([dataStr], {type: 'application/json'});
    
    const link = document.createElement('a');
    link.href = URL.createObjectURL(dataBlob);
    link.download = `tremchain-wallet-${walletAddress.substring(0, 8)}.json`;
    
    // Trigger download
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Clean up the blob URL
    URL.revokeObjectURL(link.href);
    
    showNotification('Wallet file downloaded! Store it safely offline.', 'success');
  };

  // PRODUCTION PRIVATE KEY MANAGEMENT FUNCTIONS
  const showPrivateKeyOptions = () => {
    if (!walletAddress || !walletPrivateKey) {
      showNotification('No private key available. Please create or import a wallet first.', 'error');
      return;
    }

    setShowPrivateKeySection(true);
    showNotification('Private key options are now available. Keep your key secure!', 'info');
  };

  const hidePrivateKeyOptions = () => {
    setShowPrivateKeySection(false);
    setPrivateKeyVisible(false);
  };

  const togglePrivateKeyVisibility = () => {
    if (!walletPrivateKey) {
      showNotification('No private key to display', 'error');
      return;
    }
    setPrivateKeyVisible(!privateKeyVisible);
  };

  // PRODUCTION FUNCTION - WALLET IMPORT  
  const importWallet = () => {
    const importField = document.getElementById('importPrivateKey') as HTMLInputElement;
    const privateKey = importField?.value;
    
    if (!privateKey) {
      showNotification('Please enter a private key', 'error');
      return;
    }

    // Basic validation
    if (privateKey.length < 32) {
      showNotification('Invalid private key format', 'error');
      return;
    }

    // For demo purposes, we'll create a wallet object like production
    // In a real implementation, you'd derive the public key from private key
    const importedAddress = 'imported_' + privateKey.substring(0, 10) + '...';

    setWalletAddress(importedAddress);
    setWalletPrivateKey(privateKey);
    setWalletBalance('0');

    showNotification('Wallet imported successfully!', 'success');
    
    // Clear import field
    if (importField) {
      importField.value = '';
    }
    
    // Load balance and data like production
    setTimeout(() => {
      loadBalance();
      loadMessages();
      loadEmails();
      loadConversations();
    }, 500);
  };

  // PRODUCTION FUNCTIONS - API WITH FALLBACKS
  const loadBalance = async () => {
    if (!walletAddress) return;

    try {
      // Try direct connection first
      let response;
      try {
        response = await fetch(`http://localhost:3000/api/wallet/${walletAddress}/balance`);
      } catch (directError) {
        // Fallback to proxy
        const API_BASE = window.location.origin + '/api';
        response = await fetch(`${API_BASE}/wallet/${walletAddress}/balance`);
      }
      
      if (response.ok) {
        const data = await response.json();
        setWalletBalance(data.balance || '0');
      }
    } catch (error) {
      console.error('Error loading balance:', error);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // AUTO-MINING FUNCTIONS
  const toggleAutoMine = () => {
    const newState = !autoMineEnabled;
    setAutoMineEnabled(newState);
    
    // Save preference to localStorage
    localStorage.setItem('autoMineEnabled', newState.toString());
    
    console.log('Auto-mine toggled:', newState);
    
    if (newState) {
      console.log('🔄 Auto-mining enabled - messages will be automatically mined');
      showNotification('🔄 Auto-mining enabled', 'success');
    } else {
      console.log('⏸️ Auto-mining disabled');
      showNotification('⏸️ Auto-mining disabled', 'info');
    }
  };

  const mineBlockWithResult = async () => {
    if (!walletAddress) {
      console.error('Auto-mine failed: No wallet available');
      return { success: false, error: 'No wallet available' };
    }

    try {
      console.log(`🔄 Starting auto-mine for wallet: ${walletAddress}`);
      
      // Use production API pattern
      const API_BASE = window.location.origin + '/api';
      const response = await fetch(`${API_BASE}/blockchain/mine`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ minerAddress: walletAddress })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`Auto-mine API error: ${response.status} - ${errorText}`);
        return { success: false, error: `API error: ${response.status} - ${errorText}` };
      }

      const result = await response.json();
      console.log('Auto-mine API response:', result);
      
      if (result.block || result.success) {
        // Get updated blockchain info for block number
        const API_BASE = window.location.origin + '/api';
        const blockchainResponse = await fetch(`${API_BASE}/blockchain`);
        
        const blockchainData = await blockchainResponse.json();
        
        console.log(`✅ Auto-mine successful! Block created. Chain length: ${blockchainData.length}`);
        
        // Update balance
        loadBalance();
        
        return { 
          success: true, 
          block: result.block,
          blockNumber: blockchainData.length,
          miningTime: result.miningTime || 'Unknown',
          reward: 100
        };
      } else {
        console.error('Auto-mine failed:', result.error || 'Unknown mining error');
        return { success: false, error: result.error || 'Unknown mining error' };
      }
    } catch (error) {
      console.error('Auto-mine exception:', error);
      return { success: false, error: (error as Error).message };
    }
  };

  // MANUAL MINING FUNCTION (like in production)
  const mineBlock = async () => {
    if (!walletAddress) {
      showNotification('Please create a wallet first to mine blocks', 'warning');
      return;
    }

    try {
      showNotification('⛏️ Mining block...', 'info');
      const miningResult = await mineBlockWithResult();
      
      if (miningResult.success) {
        playNotificationSound('success');
        showNotification(`🎉 Block mined successfully! Block #${miningResult.blockNumber} created`, 'success');
        
        // Reload messages and emails to show confirmed status
        setTimeout(() => {
          loadMessages();
          loadEmails();
        }, 1000);
      } else {
        showNotification(`⚠️ Mining failed: ${miningResult.error}`, 'error');
      }
    } catch (error) {
      console.error('Manual mining error:', error);
      showNotification(`⚠️ Mining failed: ${(error as Error).message}`, 'error');
    }
  };

  // NOTIFICATION SOUND SYSTEM (from production)
  const playNotificationSound = (type: 'success' | 'received' | 'sent' = 'success') => {
    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      if (type === 'sent') {
        oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
        gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.1);
      } else if (type === 'received') {
        oscillator.frequency.setValueAtTime(600, audioContext.currentTime);
        oscillator.frequency.setValueAtTime(800, audioContext.currentTime + 0.1);
        gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
      } else { // success
        oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
        gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2);
      }
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.3);
    } catch (error) {
      console.log('Notification sound not available:', error);
    }
  };

  // NO AUTOMATIC NOTIFICATIONS - Notifications removed to stop chiming
  // All notifications are now manual (only on user actions)

  // NO AUTOMATIC EMAIL NOTIFICATIONS - Notifications removed to stop chiming
  // All notifications are now manual (only on user actions)

  // NO NOTIFICATION SYSTEM - All automatic notifications disabled
  const initializeNotificationSystem = () => {
    console.log('🔕 Automatic notifications completely DISABLED - no chiming');
    // No state to set - notifications completely removed
  };

  // NO BACKGROUND CHECKING TO STOP
  const stopNotificationChecking = () => {
    // No intervals to clear since we don't do continuous checking
    console.log('📴 Notification system stopped');
  };

  // Load data when wallet is available - WITH conversation refresh
  useEffect(() => {
    if (walletAddress) {
      // Load all data when wallet becomes available
      loadMessages();
      loadEmails();
      loadConversations();
      loadBalance();
      
      // Set up conversation refresh interval (every 10 seconds for real-time updates)
      const conversationRefreshInterval = setInterval(() => {
        loadConversations(); // Refresh conversations to show new messages/emails
      }, 10000);
      
      console.log('✅ Wallet loaded - conversation auto-refresh enabled');
      
      // Cleanup interval when wallet changes
      return () => {
        clearInterval(conversationRefreshInterval);
        console.log('🔄 Conversation refresh stopped');
      };
    }
  }, [walletAddress]);

  // PROGRESSIVE THEME HANDLER
  const handleThemeLevelChange = (level: number) => {
    setThemeLevel(level);
    // Trigger the main theme toggle when crossing the middle threshold
    const newIsDarkMode = level >= 4;
    if (newIsDarkMode !== isDarkMode) {
      onThemeToggle();
    }
  };

  // THEME LEVEL DEFINITIONS (7 levels: 0=lightest, 6=darkest)
  const themeLevels = [
    { 
      bg: '#ffffff', 
      text: '#000000', 
      name: 'Brightest',
      gradient: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
    },
    { 
      bg: '#f8f9fa', 
      text: '#212529', 
      name: 'Very Light',
      gradient: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)'
    },
    { 
      bg: '#e9ecef', 
      text: '#495057', 
      name: 'Light',
      gradient: 'linear-gradient(135deg, #e9ecef 0%, #dee2e6 100%)'
    },
    { 
      bg: '#dee2e6', 
      text: '#6c757d', 
      name: 'Neutral',
      gradient: 'linear-gradient(135deg, #dee2e6 0%, #adb5bd 100%)'
    },
    { 
      bg: '#6c757d', 
      text: '#f8f9fa', 
      name: 'Medium',
      gradient: 'linear-gradient(135deg, #6c757d 0%, #495057 100%)'
    },
    { 
      bg: '#495057', 
      text: '#e9ecef', 
      name: 'Dark',
      gradient: 'linear-gradient(135deg, #495057 0%, #343a40 100%)'
    },
    { 
      bg: '#212529', 
      text: '#ffffff', 
      name: 'Darkest',
      gradient: 'linear-gradient(135deg, #212529 0%, #1a1a2e 100%)'
    }
  ];

  // Get current theme level gradient for background
  const currentThemeGradient = themeLevels[themeLevel]?.gradient || themeLevels[0].gradient;

  // RESTORE AUTO-MINING SETTING ON LOAD
  useEffect(() => {
    const savedSetting = localStorage.getItem('autoMineEnabled');
    if (savedSetting !== null) {
      const enabled = savedSetting === 'true';
      setAutoMineEnabled(enabled);
      console.log('Auto-mine setting restored:', enabled);
      
      if (enabled) {
        showNotification('🔄 Auto-mining enabled', 'success');
      }
    } else {
      // First time user - save default enabled state
      localStorage.setItem('autoMineEnabled', 'true');
      console.log('Auto-mine setting initialized: enabled by default');
      showNotification('🔄 Auto-mining enabled for seamless message confirmation', 'info');
    }
  }, []);

  // Layout toggle functions
  const switchToDesktop = () => {
    setLayoutMode('desktop');
  };

  const switchToMobile = () => {
    setLayoutMode('mobile');
  };

  const switchToMessaging = () => {
    setServiceMode('messaging');
  };

  const switchToEmail = () => {
    setServiceMode('email');
  };

  // Preserve original layout structure
  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: currentThemeGradient,
      position: 'relative',
      '&::after': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundImage: `
          radial-gradient(circle at 20% 50%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
          radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.08) 0%, transparent 50%),
          radial-gradient(circle at 40% 80%, rgba(255, 255, 255, 0.06) 0%, transparent 50%)
        `,
        zIndex: 1,
        pointerEvents: 'none'
      }
    }}>
      {/* Creative Seven-Block Progressive Theme Switcher - Mobile Responsive */}
      <Box 
        className="theme-toggle-container"
        sx={{
          position: 'fixed',
          top: { xs: 8, sm: 20 },
          right: { xs: 8, sm: 20 },
          zIndex: 1000,
          transform: { xs: 'scale(0.75)', sm: 'scale(1)' },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
            borderRadius: { xs: '16px', sm: '25px' },
            padding: { xs: '4px', sm: '6px' },
            border: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          }}
        >
          {themeLevels.map((level, index) => {
            // Alternating square sizes for visual appeal - smaller on mobile
            const blockSizesDesktop = [24, 20, 28, 18, 32, 20, 24];
            const blockSizesMobile = [16, 14, 20, 12, 22, 14, 16];
            const blockSize = {
              xs: blockSizesMobile[index],
              sm: blockSizesDesktop[index]
            };
            const isActive = themeLevel === index;
            
            return (
              <Box
                key={index}
                onClick={() => handleThemeLevelChange(index)}
                sx={{
                  width: blockSize,
                  height: blockSize,
                  backgroundColor: level.bg,
                  cursor: 'pointer',
                  marginX: { xs: 0.25, sm: 0.5 },
                  borderRadius: { xs: '4px', sm: '6px' },
                  border: isActive 
                    ? { xs: '2px solid #667eea', sm: '3px solid #667eea' }
                    : '1px solid rgba(0, 0, 0, 0.15)',
                  boxShadow: isActive 
                    ? '0 4px 12px rgba(102, 126, 234, 0.4)' 
                    : '0 2px 6px rgba(0, 0, 0, 0.1)',
                  transform: isActive 
                    ? { xs: 'scale(1.1)', sm: 'scale(1.15)' }
                    : 'scale(1)',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    transform: isActive 
                      ? { xs: 'scale(1.1)', sm: 'scale(1.15)' }
                      : { xs: 'scale(1.05)', sm: 'scale(1.05)' },
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
                  },
                  // Add connecting lines between blocks - smaller on mobile
                  '&:not(:last-child)::after': {
                    content: '""',
                    position: 'absolute',
                    right: { xs: -4, sm: -6 },
                    top: '50%',
                    width: { xs: 8, sm: 12 },
                    height: { xs: 2, sm: 3 },
                    backgroundColor: 'rgba(102, 126, 234, 0.4)',
                    borderRadius: '2px',
                    transform: 'translateY(-50%)',
                    zIndex: -1,
                  },
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  // Add subtle gradient to each block
                  background: `linear-gradient(135deg, ${level.bg} 0%, ${level.bg}dd 100%)`,
                }}
                title={`${level.name} (Level ${index})`}
              />
            );
          })}
          
          {/* Labels */}
          <Box
            sx={{
              position: 'absolute',
              bottom: { xs: -16, sm: -25 },
              left: 0,
              right: 0,
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: { xs: '6px', sm: '8px' },
              color: 'rgba(255, 255, 255, 0.7)',
              fontWeight: 500,
            }}
          >
            <span>Light</span>
            <span>Dark</span>
          </Box>
        </Box>
      </Box>

      {/* Layout Toggle - Mobile Responsive */}
      <Box 
        className="layout-toggle-container"
        sx={{
          position: 'fixed',
          top: { xs: 8, sm: 20 },
          left: { xs: 8, sm: 20 },
          zIndex: 1000,
          background: isDarkMode ? 'rgba(26, 26, 46, 0.95)' : 'rgba(255, 255, 255, 0.95)',
          borderRadius: { xs: '16px', sm: '25px' },
          padding: { xs: 0.5, sm: 1 },
          boxShadow: isDarkMode ? '0 4px 20px rgba(0,0,0,0.3)' : '0 4px 20px rgba(0,0,0,0.1)',
          backdropFilter: 'blur(10px)',
          transform: { xs: 'scale(0.85)', sm: 'scale(1)' },
        }}
      >
        <Box 
          className="layout-toggle"
          sx={{
            display: 'flex',
            background: isDarkMode ? '#2a2a4e' : '#f8f9fa',
            borderRadius: { xs: '12px', sm: '20px' },
            padding: { xs: 0.3, sm: 0.5 },
            border: isDarkMode ? '1px solid #3a3a6e' : '1px solid #e9ecef',
          }}
        >
          <Button
            onClick={switchToDesktop}
            className={layoutMode === 'desktop' ? 'layout-option active' : 'layout-option'}
            sx={{
              p: { xs: '4px 8px', sm: '8px 16px' },
              border: 'none',
              background: layoutMode === 'desktop' ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'transparent',
              color: layoutMode === 'desktop' ? 'white' : (isDarkMode ? '#b0b6c3' : '#6c757d'),
              cursor: 'pointer',
              borderRadius: { xs: '8px', sm: '16px' },
              transition: 'all 0.3s ease',
              fontSize: { xs: 10, sm: 12 },
              fontWeight: 600,
              minWidth: 'auto',
              display: 'flex',
              alignItems: 'center',
              gap: { xs: '4px', sm: '6px' },
              boxShadow: layoutMode === 'desktop' ? '0 2px 8px rgba(102, 126, 234, 0.3)' : 'none',
              '&:hover': {
                background: layoutMode === 'desktop' 
                  ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' 
                  : (isDarkMode ? 'rgba(102, 126, 234, 0.2)' : 'rgba(102, 126, 234, 0.1)'),
                color: layoutMode === 'desktop' ? 'white' : (isDarkMode ? '#e0e6ed' : '#495057')
              }
            }}
          >
            Desktop
          </Button>
          <Button
            onClick={switchToMobile}
            className={layoutMode === 'mobile' ? 'layout-option active' : 'layout-option'}
            sx={{
              p: { xs: '4px 8px', sm: '8px 16px' },
              border: 'none',
              background: layoutMode === 'mobile' ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'transparent',
              color: layoutMode === 'mobile' ? 'white' : (isDarkMode ? '#b0b6c3' : '#6c757d'),
              cursor: 'pointer',
              borderRadius: { xs: '8px', sm: '16px' },
              transition: 'all 0.3s ease',
              fontSize: { xs: 10, sm: 12 },
              fontWeight: 600,
              minWidth: 'auto',
              display: 'flex',
              alignItems: 'center',
              gap: { xs: '4px', sm: '6px' },
              boxShadow: layoutMode === 'mobile' ? '0 2px 8px rgba(102, 126, 234, 0.3)' : 'none',
              '&:hover': {
                background: layoutMode === 'mobile' 
                  ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' 
                  : (isDarkMode ? 'rgba(102, 126, 234, 0.2)' : 'rgba(102, 126, 234, 0.1)'),
                color: layoutMode === 'mobile' ? 'white' : (isDarkMode ? '#e0e6ed' : '#495057')
              }
            }}
          >
            Mobile
          </Button>
        </Box>
      </Box>

      <Container maxWidth="lg" sx={{ py: 4, position: 'relative', zIndex: 2 }}>
        {/* Header - exact original wording */}
        <Box textAlign="center" sx={{ mb: 6 }} className="header">
          <Typography
            component="h1"
            sx={{
              fontSize: 'clamp(2rem, 5vw, 3.5rem)',
              mb: 2,
              color: 'white',
              textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
              fontWeight: 400,
              position: 'relative',
              zIndex: 3,
            }}
          >
            TremChain
          </Typography>
          <Typography
            component="p"
            sx={{
              fontSize: 'clamp(1rem, 2.5vw, 1.4rem)',
              color: 'rgba(255, 255, 255, 0.9)',
              opacity: 0.9,
              position: 'relative',
              zIndex: 3,
            }}
          >
            Decentralized Messaging & Email Platform
          </Typography>
        </Box>

        {/* EXACT PRODUCTION WALLET SECTION */}
        <Card sx={{ 
          mb: 4, 
          background: isDarkMode 
            ? 'rgba(30, 30, 63, 0.7)' 
            : 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(20px)',
          border: isDarkMode 
            ? '1px solid rgba(255, 255, 255, 0.1)' 
            : '1px solid rgba(255, 255, 255, 0.2)',
          borderRadius: 3,
          boxShadow: isDarkMode 
            ? '0 10px 30px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.1)' 
            : '0 10px 30px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.3)',
          position: 'relative',
          zIndex: 2,
        }}>
          <CardContent>
            <Typography variant="h5" sx={{ 
              color: isDarkMode ? '#e0e6ed' : '#667eea', 
              mb: 3, 
              fontWeight: 600 
            }}>
              Wallet Management
            </Typography>
            
            {/* Production 2-column grid layout */}
            <Grid container spacing={4}>
              {/* Left Column - Create Wallet & Wallet Display */}
              <Grid item xs={12} md={6}>
                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<WalletIcon />}
                  onClick={createWallet}
                  disabled={loadingState.wallet}
                  sx={{ 
                    mb: 2,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    '&:hover': { 
                      opacity: 0.9,
                      transform: 'translateY(-1px)',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                    }
                  }}
                >
                  {loadingState.wallet ? 'Creating...' : 'Create New Wallet'}
                </Button>

                {/* Wallet Info Section - Production Style */}
                {walletAddress && (
                  <Box sx={{
                    background: 'rgba(248, 249, 250, 0.1)',
                    borderRadius: '8px',
                    padding: 2,
                    mt: 2,
                    borderLeft: '4px solid #667eea',
                    width: '100%',
                    maxWidth: '100%',
                    boxSizing: 'border-box',
                    overflow: 'hidden'
                  }}>
                    <Typography variant="h6" sx={{ color: isDarkMode ? '#e0e6ed' : '#495057', mb: 2 }}>Your Wallet</Typography>
                    
                    {/* Address with inline copy button - Production Style */}
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" sx={{ color: isDarkMode ? '#e0e6ed' : '#495057', mb: 1 }}><strong>Address:</strong></Typography>
                      <Box sx={{
                        position: 'relative',
                        display: 'grid',
                        gridTemplateColumns: '1fr auto',
                        gap: 1,
                        width: '100%',
                        maxWidth: '100%',
                        alignItems: 'flex-start',
                        overflow: 'hidden'
                      }}>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: isDarkMode ? '#e0e6ed' : '#495057',
                            wordBreak: 'break-all',
                            fontSize: '0.85rem',
                            fontFamily: 'monospace'
                          }}
                        >
                          {walletAddress}
                        </Typography>
                        <Button
                          size="small"
                          startIcon={<CopyIcon />}
                          onClick={copyAddressToClipboard}
                          sx={{
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            color: 'white',
                            fontSize: '0.75rem',
                            minWidth: 'auto',
                            px: 1.5,
                            py: 0.5,
                            ml: 1,
                            borderRadius: 1.5,
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            '&:hover': { 
                              background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4c93 100%)',
                              transform: 'translateY(-1px)',
                              boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)'
                            },
                            '&:active': {
                              transform: 'translateY(0)',
                              boxShadow: '0 1px 4px rgba(102, 126, 234, 0.3)'
                            }
                          }}
                        >
                          Copy
                        </Button>
                      </Box>
                    </Box>

                    {/* Balance */}
                    <Typography variant="body2" sx={{ color: isDarkMode ? '#e0e6ed' : '#495057', mb: 2 }}>
                      <strong>Balance:</strong> {walletBalance} TRC
                    </Typography>

                    {/* Private Key Section - Exact Production Structure */}
                    {showPrivateKeySection && (
                      <Box sx={{
                        mt: 2.5,
                        pt: 2.5,
                        borderTop: '2px solid rgba(233, 236, 239, 0.2)'
                      }}>
                        <Typography variant="body2" sx={{ color: isDarkMode ? '#e0e6ed' : '#495057', mb: 1 }}><strong>Private Key:</strong></Typography>
                        
                        <Box sx={{
                          position: 'relative',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 1.25,
                          mt: 1.25
                        }}>
                          {/* Private key display - fixed overflow */}
                          <Box sx={{
                            fontFamily: 'Courier New, monospace',
                            fontSize: '0.75rem', // Smaller font to prevent overflow
                            background: privateKeyVisible ? 'rgba(255, 243, 205, 0.3)' : 'rgba(248, 249, 250, 0.3)',
                            padding: 1.5,
                            borderRadius: 1,
                            border: privateKeyVisible ? '2px solid #ffc107' : '2px solid rgba(233, 236, 239, 0.4)',
                            wordBreak: 'break-all',
                            wordWrap: 'break-word', // Better word wrapping
                            overflowWrap: 'break-word', // Ensure long strings break
                            minHeight: '44px',
                            maxWidth: '100%', // Prevent overflow
                            display: 'flex',
                            alignItems: 'center',
                            color: privateKeyVisible 
                              ? (isDarkMode ? '#e0e6ed' : '#856404') // Match wallet address color in dark mode
                              : (isDarkMode ? '#e0e6ed' : '#495057'),
                            userSelect: privateKeyVisible ? 'text' : 'none',
                            transition: 'all 0.3s ease',
                            overflow: 'hidden', // Hide any overflow
                            lineHeight: 1.3 // Better line spacing for wrapped text
                          }}>
                            {privateKeyVisible 
                              ? walletPrivateKey 
                              : '••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••'
                            }
                          </Box>
                          
                          {/* Action buttons - Modern smooth icons */}
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                            <Button
                              size="small"
                              onClick={togglePrivateKeyVisibility}
                              sx={{
                                background: 'linear-gradient(135deg, #6c757d 0%, #5a6268 100%)',
                                color: 'white',
                                minWidth: '44px',
                                minHeight: '44px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: 2,
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                '&:hover': { 
                                  background: 'linear-gradient(135deg, #5a6268 0%, #495057 100%)',
                                  transform: 'translateY(-1px)',
                                  boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
                                },
                                '&:active': {
                                  transform: 'translateY(0)',
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                }
                              }}
                              title={privateKeyVisible ? 'Hide private key' : 'Show private key'}
                            >
                              {privateKeyVisible ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                            </Button>
                            <Button
                              size="small"
                              onClick={copyPrivateKey}
                              sx={{
                                background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)',
                                color: 'white',
                                minWidth: '44px',
                                minHeight: '44px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: 2,
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                '&:hover': { 
                                  background: 'linear-gradient(135deg, #20c997 0%, #17a2b8 100%)',
                                  transform: 'translateY(-1px)',
                                  boxShadow: '0 4px 8px rgba(40, 167, 69, 0.3)'
                                },
                                '&:active': {
                                  transform: 'translateY(0)',
                                  boxShadow: '0 2px 4px rgba(40, 167, 69, 0.3)'
                                }
                              }}
                              title="Copy private key"
                            >
                              <CopyIcon fontSize="small" />
                            </Button>
                            <Button
                              size="small"
                              onClick={downloadPrivateKey}
                              sx={{
                                background: 'linear-gradient(135deg, #17a2b8 0%, #6f42c1 100%)',
                                color: 'white',
                                minWidth: '44px',
                                minHeight: '44px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: 2,
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                '&:hover': { 
                                  background: 'linear-gradient(135deg, #6f42c1 0%, #e83e8c 100%)',
                                  transform: 'translateY(-1px)',
                                  boxShadow: '0 4px 8px rgba(23, 162, 184, 0.3)'
                                },
                                '&:active': {
                                  transform: 'translateY(0)',
                                  boxShadow: '0 2px 4px rgba(23, 162, 184, 0.3)'
                                }
                              }}
                              title="Download wallet backup"
                            >
                              <DownloadIcon fontSize="small" />
                            </Button>
                          </Box>
                        </Box>
                        
                        {/* Warning - Enhanced visibility for dark mode */}
                        <Box sx={{
                          background: isDarkMode 
                            ? 'rgba(255, 193, 7, 0.15)' 
                            : 'rgba(255, 243, 205, 0.3)',
                          color: isDarkMode 
                            ? '#ffc107' 
                            : '#856404',
                          padding: 1.25,
                          borderRadius: 1,
                          borderLeft: isDarkMode 
                            ? '4px solid #ffc107' 
                            : '4px solid #ffc107',
                          fontSize: '0.9rem',
                          mt: 1.25,
                          display: 'flex',
                          alignItems: 'center',
                          fontWeight: isDarkMode ? 600 : 400, // Bold in dark mode for better visibility
                        }}>
                          ⚠️ Keep your private key safe and never share it with anyone!
                        </Box>
                      </Box>
                    )}

                    {/* Backup button and Auto-mining toggle - Modern smooth icons */}
                    <Box sx={{ mt: 2, display: 'flex', gap: 1.25, flexWrap: 'wrap', alignItems: 'center' }}>
                      <Button
                        variant="outlined"
                        startIcon={showPrivateKeySection ? <LockIcon /> : <SecurityIcon />}
                        onClick={showPrivateKeySection ? hidePrivateKeyOptions : showPrivateKeyOptions}
                        sx={{
                          borderColor: isDarkMode ? '#e0e6ed' : '#495057',
                          color: isDarkMode ? '#e0e6ed' : '#495057',
                          borderRadius: 2,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': { 
                            borderColor: showPrivateKeySection ? '#dc3545' : '#667eea',
                            backgroundColor: showPrivateKeySection 
                              ? 'rgba(220, 53, 69, 0.1)' 
                              : 'rgba(102, 126, 234, 0.1)',
                            color: showPrivateKeySection ? '#dc3545' : '#667eea',
                            transform: 'translateY(-1px)',
                            boxShadow: showPrivateKeySection 
                              ? '0 2px 8px rgba(220, 53, 69, 0.2)' 
                              : '0 2px 8px rgba(102, 126, 234, 0.2)'
                          },
                          '&:active': {
                            transform: 'translateY(0)'
                          }
                        }}
                      >
                        {showPrivateKeySection ? 'Hide Private Key' : 'Backup Private Key'}
                      </Button>

                    </Box>
                  </Box>
                )}
              </Grid>

              {/* Right Column - Import Wallet - Production Style */}
              <Grid item xs={12} md={6}>
                <Box>
                  <Typography variant="body1" sx={{ color: isDarkMode ? '#e0e6ed' : '#495057', mb: 1, fontWeight: 600 }}>
                    Import Private Key:
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder="Enter private key"
                    id="importPrivateKey"
                    sx={{
                      mb: 2,
                      '& .MuiInputLabel-root': { 
                        color: isDarkMode ? '#b0b6c3' : '#6c757d'
                      },
                      '& .MuiOutlinedInput-root': { 
                        color: isDarkMode ? '#e0e6ed' : '#495057',
                        background: isDarkMode 
                          ? 'rgba(255, 255, 255, 0.05)' 
                          : 'rgba(255, 255, 255, 0.8)',
                        backdropFilter: 'blur(10px)',
                        '& fieldset': { 
                          borderColor: isDarkMode ? '#3a3a6e' : '#ced4da'
                        },
                        '&:hover fieldset': { 
                          borderColor: isDarkMode ? '#7c8bf0' : '#667eea'
                        },
                        '&.Mui-focused fieldset': { 
                          borderColor: isDarkMode ? '#7c8bf0' : '#667eea'
                        }
                      }
                    }}
                  />
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={importWallet}
                    disabled={loadingState.wallet}
                    sx={{ 
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      '&:hover': { 
                        opacity: 0.9,
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                      }
                    }}
                  >
                    Import Wallet
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Layout Mode Conditional Rendering */}
        {layoutMode === 'desktop' ? (
          // Desktop Layout - TremSBC Style
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: '350px 1fr', 
            gap: 2.5, 
            mb: 5, 
            minHeight: '600px',
            background: isDarkMode 
              ? 'rgba(30, 30, 63, 0.7)' 
              : 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(20px)',
            border: isDarkMode 
              ? '1px solid rgba(255, 255, 255, 0.1)' 
              : '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: 3,
            boxShadow: isDarkMode 
              ? '0 10px 30px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.1)' 
              : '0 10px 30px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.3)',
            overflow: 'hidden',
            position: 'relative',
            zIndex: 2,
          }}>
            {/* Left Pane - Sidebar */}
            <Box sx={{ 
              background: isDarkMode ? '#252547' : '#f8f9fa',
              borderRight: isDarkMode ? '2px solid #3a3a6e' : '2px solid #e9ecef',
              display: 'flex',
              flexDirection: 'column',
              p: 3
            }}>
              {/* Service Toggle */}
              <Box sx={{ mb: 3 }}>
                <Button 
                  onClick={switchToMessaging}
                  fullWidth
                  sx={{
                    mb: 1,
                    background: serviceMode === 'messaging' 
                      ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' 
                      : 'transparent',
                    color: serviceMode === 'messaging' 
                      ? 'white' 
                      : (isDarkMode ? '#b0b6c3' : '#6c757d'),
                    '&:hover': {
                      background: serviceMode === 'messaging' 
                        ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' 
                        : (isDarkMode ? 'rgba(102, 126, 234, 0.2)' : 'rgba(102, 126, 234, 0.1)')
                    }
                  }}
                >
                  💬 Messages
                </Button>
                <Button 
                  onClick={switchToEmail}
                  fullWidth
                  sx={{
                    background: serviceMode === 'email' 
                      ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' 
                      : 'transparent',
                    color: serviceMode === 'email' 
                      ? 'white' 
                      : (isDarkMode ? '#b0b6c3' : '#6c757d'),
                    '&:hover': {
                      background: serviceMode === 'email' 
                        ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' 
                        : (isDarkMode ? 'rgba(102, 126, 234, 0.2)' : 'rgba(102, 126, 234, 0.1)')
                    }
                  }}
                >
                  📧 Emails
                </Button>
              </Box>
              
              {/* Conversations List */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" sx={{ 
                  color: isDarkMode ? '#7c8bf0' : '#667eea', 
                  fontSize: '1rem'
                }}>
                  Conversations
                </Typography>
                <Box
                  onClick={startNewConversation}
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    background: isDarkMode 
                      ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' 
                      : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    color: 'white',
                    fontSize: '18px',
                    fontWeight: 'bold',
                    boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'scale(1.05)',
                      boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
                    }
                  }}
                >
                  +
                </Box>
              </Box>
              {/* Conversations List (like original app) */}
              {conversations.length > 0 ? (
                <Box sx={{ maxHeight: '300px', overflowY: 'auto' }}>
                  {conversations.map((conversation, index) => (
                    <Box
                      key={index}
                      onClick={() => {
                        setMessageToAddress(conversation.address);
                        setEmailToAddress(conversation.address);
                        loadConversationMessages(conversation.address);
                        showNotification(`📝 Loaded unified conversation with ${conversation.address.substring(0, 12)}... (${conversation.hasMessages ? 'Messages' : ''}${conversation.hasMessages && conversation.hasEmails ? ' + ' : ''}${conversation.hasEmails ? 'Emails' : ''})`, 'info');
                      }}
                      sx={{
                        background: currentConversation === conversation.address 
                          ? 'rgba(102, 126, 234, 0.2)' 
                          : isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.5)',
                        p: 2,
                        mb: 1,
                        borderRadius: 2,
                        backdropFilter: 'blur(10px)',
                        border: currentConversation === conversation.address 
                          ? '1px solid rgba(102, 126, 234, 0.5)' 
                          : '1px solid rgba(255,255,255,0.1)',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          background: 'rgba(102, 126, 234, 0.1)',
                          transform: 'translateX(2px)',
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" sx={{ 
                          color: isDarkMode ? '#e0e6ed' : '#495057',
                          fontWeight: 600
                        }}>
                          {conversation.address.substring(0, 12)}...
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          {conversation.hasMessages && (
                            <Typography sx={{ fontSize: '0.8rem' }}>💬</Typography>
                          )}
                          {conversation.hasEmails && (
                            <Typography sx={{ fontSize: '0.8rem' }}>📧</Typography>
                          )}
                        </Box>
                      </Box>
                      <Typography variant="caption" sx={{ 
                        color: isDarkMode ? '#b0b6c3' : '#6c757d',
                        display: 'block',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {conversation.lastActivity.type === 'email' 
                          ? `📧 ${conversation.lastActivity.subject || conversation.lastActivity.data?.subject || 'Email'}` 
                          : `💬 ${conversation.lastActivity.content || 'Message'}`}
                      </Typography>
                      <Typography variant="caption" sx={{ 
                        color: isDarkMode ? '#9ca3af' : '#8e9aaf',
                        fontSize: '0.7rem',
                        float: 'right'
                      }}>
                        {new Date(conversation.lastActivity.timestamp).toLocaleDateString()}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Box sx={{ 
                  background: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.5)',
                  p: 2,
                  borderRadius: 2,
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255,255,255,0.1)'
                }}>
                  <Typography variant="body2" sx={{ 
                    color: isDarkMode ? '#b0b6c3' : '#6c757d',
                    fontStyle: 'italic'
                  }}>
                    No conversations yet
                  </Typography>
                  <Typography variant="caption" sx={{ 
                    color: isDarkMode ? '#9ca3af' : '#495057'
                  }}>
                    Send a message to start...
                  </Typography>
                </Box>
              )}
            </Box>
            
            {/* Right Pane - Chat Area */}
            <Box sx={{ 
              background: isDarkMode ? '#1e1e3f' : 'white',
              p: 4,
              display: 'flex',
              flexDirection: 'column'
            }}>
              {serviceMode === 'messaging' ? (
                // Messaging Interface
                <>
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    mb: 3
                  }}>
                    <Typography variant="h5" sx={{ 
                      color: isDarkMode ? '#7c8bf0' : '#667eea', 
                      fontWeight: 600 
                    }}>
                      {currentConversation ? `Chat with ${currentConversation.substring(0, 12)}...` : 'New TremSBCMsg'}
                    </Typography>
                    {!currentConversation && (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={startNewConversation}
                        sx={{ 
                          borderColor: isDarkMode ? '#7c8bf0' : '#667eea',
                          color: isDarkMode ? '#7c8bf0' : '#667eea',
                          minWidth: 'auto',
                          px: 2,
                          py: 0.5,
                          fontSize: '0.75rem',
                          '&:hover': { 
                            borderColor: isDarkMode ? '#9ca3f0' : '#7c8bf0',
                            backgroundColor: isDarkMode ? 'rgba(124, 139, 240, 0.1)' : 'rgba(102, 126, 234, 0.1)'
                          }
                        }}
                      >
                        🔄 Clear Form
                      </Button>
                    )}
                    {currentConversation && (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={startNewConversation}
                        sx={{ 
                          borderColor: isDarkMode ? '#7c8bf0' : '#667eea',
                          color: isDarkMode ? '#7c8bf0' : '#667eea',
                          minWidth: 'auto',
                          px: 2,
                          py: 0.5,
                          fontSize: '0.75rem',
                          '&:hover': { 
                            borderColor: isDarkMode ? '#9ca3f0' : '#7c8bf0',
                            backgroundColor: isDarkMode ? 'rgba(124, 139, 240, 0.1)' : 'rgba(102, 126, 234, 0.1)'
                          }
                        }}
                      >
                        + New Message
                      </Button>
                    )}
                  </Box>
                  
                  {/* To: Address Input Field - Fixed at Top */}
                  <Box sx={{ mb: 2 }}>
                    <TextField
                      fullWidth
                      label="To: Enter wallet address"
                      placeholder="Enter recipient wallet address"
                      value={messageToAddress}
                      onChange={(e) => setMessageToAddress(e.target.value)}
                      sx={{ 
                        '& .MuiInputLabel-root': { 
                          color: isDarkMode ? '#b0b6c3' : '#6c757d'
                        },
                        '& .MuiOutlinedInput-root': { 
                          color: isDarkMode ? '#e0e6ed' : '#495057',
                          background: isDarkMode 
                            ? 'rgba(255, 255, 255, 0.05)' 
                            : 'rgba(255, 255, 255, 0.8)',
                          backdropFilter: 'blur(10px)',
                          '& fieldset': { 
                            borderColor: isDarkMode ? '#3a3a6e' : '#ced4da'
                          },
                          '&:hover fieldset': { 
                            borderColor: isDarkMode ? '#7c8bf0' : '#667eea'
                          },
                          '&.Mui-focused fieldset': { 
                            borderColor: isDarkMode ? '#7c8bf0' : '#667eea'
                          }
                        }
                      }}
                    />
                  </Box>
                  
                  {/* Persistent To: Address Display */}
                  {currentConversation && (
                    <Box sx={{ 
                      mb: 2,
                      p: 2,
                      background: isDarkMode ? 'rgba(102, 126, 234, 0.1)' : 'rgba(102, 126, 234, 0.05)',
                      borderRadius: 2,
                      border: '1px solid rgba(102, 126, 234, 0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}>
                      <Box>
                        <Typography variant="body2" sx={{ 
                          color: isDarkMode ? '#7c8bf0' : '#667eea',
                          fontWeight: 600,
                          mb: 0.5
                        }}>
                          Conversation with:
                        </Typography>
                        <Typography variant="body1" sx={{ 
                          color: isDarkMode ? '#e0e6ed' : '#495057',
                          fontFamily: 'monospace',
                          fontSize: '0.9rem',
                          wordBreak: 'break-all'
                        }}>
                          {currentConversation}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="caption" sx={{ 
                          color: isDarkMode ? '#b0b6c3' : '#6c757d',
                          fontSize: '0.7rem'
                        }}>
                          {conversationMessages.filter(m => m.type === 'message').length} messages
                        </Typography>
                        <Typography variant="caption" sx={{ 
                          color: isDarkMode ? '#b0b6c3' : '#6c757d',
                          fontSize: '0.7rem'
                        }}>
                          {conversationMessages.filter(m => m.type === 'email').length} emails
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  
                  {/* Conversation Messages Display (like original app) */}
                  {currentConversation && conversationMessages.length > 0 && (
                    <Box sx={{ 
                      maxHeight: '300px', 
                      overflowY: 'auto', 
                      mb: 3,
                      p: 2,
                      background: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                      borderRadius: 2,
                      border: '1px solid rgba(255,255,255,0.1)'
                    }}>
                      {conversationMessages.map((item, index) => {
                        // UNIFIED THREAD: Handle both messages and emails like iMessage/WhatsApp
                        const isEmail = item.type === 'email';
                        const isMessage = item.type === 'message';
                        const isConfirmed = item.status === 'confirmed' || item.confirmed || item.blockHeight;
                        const isOwn = item.from === walletAddress;
                        
                        // Get content based on type
                        const getContent = () => {
                          if (!isConfirmed) {
                            return isOwn 
                              ? `⏳ ${isEmail ? 'Email' : 'Message'} pending blockchain confirmation...`
                              : `🔐 Encrypted ${isEmail ? 'email' : 'message'} - awaiting decryption...`;
                          }
                          
                          if (isEmail) {
                            const subject = item.data?.subject || item.subject || 'No Subject';
                            const body = item.data?.body || item.body || '';
                            return `📧 ${subject}\n${body}`;
                          }
                          
                          // For messages, check if content looks encrypted and show appropriate message
                          const messageContent = item.content || 'No content';
                          
                          // Check if the content looks like encrypted data (base64-like or very long random string)
                          const isEncryptedContent = messageContent.length > 100 && 
                                                   (messageContent.includes('==') || 
                                                    messageContent.match(/^[A-Za-z0-9+/=]+$/) ||
                                                    messageContent === '[Encrypted Message]');
                          
                          if (isEncryptedContent && !isOwn) {
                            return '🔐 Message content encrypted - unable to decrypt';
                          }
                          
                          return messageContent;
                        };
                        
                        return (
                          <Box key={`${item.type}-${index}`} sx={{ 
                            mb: 2,
                            display: 'flex',
                            justifyContent: isOwn ? 'flex-end' : 'flex-start'
                          }}>
                            <Box sx={{
                              maxWidth: '70%',
                              p: 2,
                              borderRadius: 2,
                              background: isOwn 
                                ? (isConfirmed 
                                    ? (isEmail 
                                        ? 'linear-gradient(135deg, #28a745 0%, #20c997 100%)' // Green for emails
                                        : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)') // Blue for messages
                                    : 'linear-gradient(135deg, #ffa726 0%, #ff8f00 100%)') // Orange for pending
                                : isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                              color: isOwn 
                                ? 'white'
                                : isDarkMode ? '#e0e6ed' : '#495057',
                              opacity: isConfirmed ? 1 : 0.7,
                              border: !isConfirmed ? '1px dashed rgba(255,165,0,0.5)' : (isEmail && isOwn ? '2px solid rgba(40, 167, 69, 0.3)' : 'none'),
                              position: 'relative'
                            }}>
                              {/* Type Indicator */}
                              {isConfirmed && (
                                <Typography variant="caption" sx={{ 
                                  position: 'absolute',
                                  top: -8,
                                  right: 8,
                                  background: isEmail ? '#28a745' : '#667eea',
                                  color: 'white',
                                  px: 1,
                                  py: 0.25,
                                  borderRadius: 1,
                                  fontSize: '0.65rem',
                                  fontWeight: 600
                                }}>
                                  {isEmail ? '📧' : '💬'}
                                </Typography>
                              )}
                              
                              <Typography variant="body2" sx={{ mb: 0.5, whiteSpace: 'pre-wrap' }}>
                                {getContent()}
                              </Typography>
                              
                              <Typography variant="caption" sx={{ 
                                opacity: 0.7,
                                fontSize: '0.7rem',
                                display: 'block'
                              }}>
                                {new Date(item.timestamp).toLocaleString()}
                                {isConfirmed ? ' ✅ Confirmed' : ' ⏳ Pending'}
                              </Typography>
                              
                              {/* Reply Buttons */}
                              {item.from !== walletAddress && isConfirmed && (
                                <Box sx={{ mt: 0.5 }}>
                                  <Button 
                                    size="small" 
                                    onClick={() => {
                                      if (isEmail) {
                                        // Reply to email
                                        setEmailToAddress(item.from);
                                        const originalSubject = item.data?.subject || item.subject || 'No Subject';
                                        const replySubject = originalSubject.startsWith('Re: ') ? originalSubject : `Re: ${originalSubject}`;
                                        setEmailSubject(replySubject);
                                        setEmailBody(`\n\n--- Original Email ---\nFrom: ${item.from}\nSubject: ${originalSubject}\n${item.data?.body || item.body || ''}`);
                                        showNotification('📝 Email reply prepared - add your response', 'info');
                                      } else {
                                        // Reply to message
                                        setMessageToAddress(item.from);
                                        setMessageContent(`--- Replying to message from ${item.from.substring(0, 12)}... ---\n"${item.content}"\n\n`);
                                        showNotification('📝 Message reply prepared - add your response', 'info');
                                      }
                                    }}
                                    sx={{ 
                                      mr: 1,
                                      minWidth: 'auto', 
                                      p: 0.5,
                                      color: 'rgba(102, 126, 234, 0.8)',
                                      fontSize: '0.6rem'
                                    }}
                                  >
                                    ↩️ Reply {isEmail ? 'Email' : 'Msg'}
                                  </Button>
                                </Box>
                              )}
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                  
                  {/* Messaging Form */}
                  <Box sx={{ mb: 3 }}>
                    
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      label="TremSBCMsg"
                      placeholder="Enter your TremSBCMsg message"
                      value={messageContent}
                      onChange={(e) => setMessageContent(e.target.value)}
                      sx={{ 
                        mb: 2,
                        '& .MuiInputLabel-root': { 
                          color: isDarkMode ? '#b0b6c3' : '#6c757d'
                        },
                        '& .MuiOutlinedInput-root': { 
                          color: isDarkMode ? '#e0e6ed' : '#495057',
                          background: isDarkMode 
                            ? 'rgba(255, 255, 255, 0.05)' 
                            : 'rgba(255, 255, 255, 0.8)',
                          backdropFilter: 'blur(10px)',
                          '& fieldset': { 
                            borderColor: isDarkMode ? '#3a3a6e' : '#ced4da'
                          },
                          '&:hover fieldset': { 
                            borderColor: isDarkMode ? '#7c8bf0' : '#667eea'
                          },
                          '&.Mui-focused fieldset': { 
                            borderColor: isDarkMode ? '#7c8bf0' : '#667eea'
                          }
                        }
                      }}
                    />
                  </Box>
                  
                  {/* Auto-mining Toggle - Messaging */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                    <Typography variant="body2" sx={{ 
                      color: isDarkMode ? '#e0e6ed' : '#495057',
                      fontWeight: 500 
                    }}>
                      Auto-mine:
                    </Typography>
                    <Box
                      onClick={toggleAutoMine}
                      sx={{
                        width: 48,
                        height: 24,
                        borderRadius: '12px',
                        background: autoMineEnabled 
                          ? 'linear-gradient(135deg, #28a745 0%, #20c997 100%)' 
                          : 'linear-gradient(135deg, #6c757d 0%, #adb5bd 100%)',
                        cursor: 'pointer',
                        position: 'relative',
                        boxShadow: autoMineEnabled 
                          ? '0 2px 8px rgba(40, 167, 69, 0.3)' 
                          : '0 2px 8px rgba(108, 117, 125, 0.2)',
                        transition: 'all 0.3s ease',
                      }}
                    >
                      <Box
                        sx={{
                          width: 18,
                          height: 18,
                          borderRadius: '50%',
                          background: 'white',
                          position: 'absolute',
                          top: '3px',
                          left: autoMineEnabled ? '27px' : '3px',
                          transition: 'all 0.3s ease',
                          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                        }}
                      />
                    </Box>
                  </Box>
                  
                  {/* Manual Mine Button (when auto-mining is off) */}
                  {!autoMineEnabled && (
                    <Button
                      variant="outlined"
                      onClick={mineBlock}
                      sx={{ 
                        borderColor: isDarkMode ? '#7c8bf0' : '#667eea',
                        color: isDarkMode ? '#7c8bf0' : '#667eea',
                        mb: 2,
                        '&:hover': { 
                          borderColor: isDarkMode ? '#9ca3f0' : '#7c8bf0',
                          backgroundColor: isDarkMode ? 'rgba(124, 139, 240, 0.1)' : 'rgba(102, 126, 234, 0.1)'
                        }
                      }}
                    >
                      ⛏️ Mine Block
                    </Button>
                  )}
                  
                  <Button
                    variant="contained"
                    startIcon={<SendIcon />}
                    onClick={sendMessage}
                    disabled={loadingState.message}
                    sx={{ 
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      alignSelf: 'flex-start',
                      '&:hover': { 
                        opacity: 0.9,
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                      }
                    }}
                  >
                    {loadingState.message ? 'Sending...' : 'Send TremSBCMsg'}
                  </Button>
                </>
              ) : (
                // Email Interface
                <>
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    mb: 3
                  }}>
                    <Typography variant="h5" sx={{ 
                      color: isDarkMode ? '#7c8bf0' : '#667eea', 
                      fontWeight: 600 
                    }}>
                      New TremSBCMail
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={startNewConversation}
                      sx={{ 
                        borderColor: isDarkMode ? '#7c8bf0' : '#667eea',
                        color: isDarkMode ? '#7c8bf0' : '#667eea',
                        minWidth: 'auto',
                        px: 2,
                        py: 0.5,
                        fontSize: '0.75rem',
                        '&:hover': { 
                          borderColor: isDarkMode ? '#9ca3f0' : '#7c8bf0',
                          backgroundColor: isDarkMode ? 'rgba(124, 139, 240, 0.1)' : 'rgba(102, 126, 234, 0.1)'
                        }
                      }}
                    >
                      + New Email
                    </Button>
                  </Box>
                  
                  {/* Email Form */}
                  <Box sx={{ mb: 3 }}>
                    <TextField
                      fullWidth
                      label="To: Enter wallet address"
                      placeholder="Enter recipient wallet address"
                      value={emailToAddress}
                      onChange={(e) => setEmailToAddress(e.target.value)}
                      sx={{ 
                        mb: 2,
                        '& .MuiInputLabel-root': { 
                          color: isDarkMode ? '#b0b6c3' : '#6c757d'
                        },
                        '& .MuiOutlinedInput-root': { 
                          color: isDarkMode ? '#e0e6ed' : '#495057',
                          background: isDarkMode 
                            ? 'rgba(255, 255, 255, 0.05)' 
                            : 'rgba(255, 255, 255, 0.8)',
                          backdropFilter: 'blur(10px)',
                          '& fieldset': { 
                            borderColor: isDarkMode ? '#3a3a6e' : '#ced4da'
                          },
                          '&:hover fieldset': { 
                            borderColor: isDarkMode ? '#7c8bf0' : '#667eea'
                          },
                          '&.Mui-focused fieldset': { 
                            borderColor: isDarkMode ? '#7c8bf0' : '#667eea'
                          }
                        }
                      }}
                    />
                    
                    <TextField
                      fullWidth
                      label="Subject"
                      placeholder="Subject"
                      value={emailSubject}
                      onChange={(e) => setEmailSubject(e.target.value)}
                      sx={{ 
                        mb: 2,
                        '& .MuiInputLabel-root': { 
                          color: isDarkMode ? '#b0b6c3' : '#6c757d'
                        },
                        '& .MuiOutlinedInput-root': { 
                          color: isDarkMode ? '#e0e6ed' : '#495057',
                          background: isDarkMode 
                            ? 'rgba(255, 255, 255, 0.05)' 
                            : 'rgba(255, 255, 255, 0.8)',
                          backdropFilter: 'blur(10px)',
                          '& fieldset': { 
                            borderColor: isDarkMode ? '#3a3a6e' : '#ced4da'
                          },
                          '&:hover fieldset': { 
                            borderColor: isDarkMode ? '#7c8bf0' : '#667eea'
                          },
                          '&.Mui-focused fieldset': { 
                            borderColor: isDarkMode ? '#7c8bf0' : '#667eea'
                          }
                        }
                      }}
                    />
                    
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      label="TremSBCMail message"
                      placeholder="Enter your TremSBCMail message"
                      value={emailBody}
                      onChange={(e) => setEmailBody(e.target.value)}
                      sx={{ 
                        mb: 2,
                        '& .MuiInputLabel-root': { 
                          color: isDarkMode ? '#b0b6c3' : '#6c757d'
                        },
                        '& .MuiOutlinedInput-root': { 
                          color: isDarkMode ? '#e0e6ed' : '#495057',
                          background: isDarkMode 
                            ? 'rgba(255, 255, 255, 0.05)' 
                            : 'rgba(255, 255, 255, 0.8)',
                          backdropFilter: 'blur(10px)',
                          '& fieldset': { 
                            borderColor: isDarkMode ? '#3a3a6e' : '#ced4da'
                          },
                          '&:hover fieldset': { 
                            borderColor: isDarkMode ? '#7c8bf0' : '#667eea'
                          },
                          '&.Mui-focused fieldset': { 
                            borderColor: isDarkMode ? '#7c8bf0' : '#667eea'
                          }
                        }
                      }}
                    />
                  </Box>
                  
                  {/* Auto-mining Toggle - Email */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                    <Typography variant="body2" sx={{ 
                      color: isDarkMode ? '#e0e6ed' : '#495057',
                      fontWeight: 500 
                    }}>
                      Auto-mine:
                    </Typography>
                    <Box
                      onClick={toggleAutoMine}
                      sx={{
                        width: 48,
                        height: 24,
                        borderRadius: '12px',
                        background: autoMineEnabled 
                          ? 'linear-gradient(135deg, #28a745 0%, #20c997 100%)' 
                          : 'linear-gradient(135deg, #6c757d 0%, #adb5bd 100%)',
                        cursor: 'pointer',
                        position: 'relative',
                        boxShadow: autoMineEnabled 
                          ? '0 2px 8px rgba(40, 167, 69, 0.3)' 
                          : '0 2px 8px rgba(108, 117, 125, 0.2)',
                        transition: 'all 0.3s ease',
                      }}
                    >
                      <Box
                        sx={{
                          width: 18,
                          height: 18,
                          borderRadius: '50%',
                          background: 'white',
                          position: 'absolute',
                          top: '3px',
                          left: autoMineEnabled ? '27px' : '3px',
                          transition: 'all 0.3s ease',
                          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                        }}
                      />
                    </Box>
                  </Box>
                  
                  {/* Manual Mine Button (when auto-mining is off) */}
                  {!autoMineEnabled && (
                    <Button
                      variant="outlined"
                      onClick={mineBlock}
                      sx={{ 
                        borderColor: isDarkMode ? '#7c8bf0' : '#667eea',
                        color: isDarkMode ? '#7c8bf0' : '#667eea',
                        mb: 2,
                        '&:hover': { 
                          borderColor: isDarkMode ? '#9ca3f0' : '#7c8bf0',
                          backgroundColor: isDarkMode ? 'rgba(124, 139, 240, 0.1)' : 'rgba(102, 126, 234, 0.1)'
                        }
                      }}
                    >
                      ⛏️ Mine Block
                    </Button>
                  )}
                  
                  <Button
                    variant="contained"
                    startIcon={<EmailIcon />}
                    onClick={sendEmail}
                    disabled={loadingState.email}
                    sx={{ 
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      alignSelf: 'flex-start',
                      '&:hover': { 
                        opacity: 0.9,
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                      }
                    }}
                  >
                    {loadingState.email ? 'Sending...' : 'Send TremSBCMail'}
                  </Button>
                </>
              )}
            </Box>
          </Box>
        ) : (
          // Mobile Layout - Cards Style
          <>
            {/* Mobile Service Toggle */}
            <Card sx={{ 
              mb: 4, 
              background: isDarkMode 
                ? 'rgba(30, 30, 63, 0.7)' 
                : 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(20px)',
              border: isDarkMode 
                ? '1px solid rgba(255, 255, 255, 0.1)' 
                : '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: 3,
              boxShadow: isDarkMode 
                ? '0 10px 30px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.1)' 
                : '0 10px 30px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.3)',
              position: 'relative',
              zIndex: 2,
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <Button 
                    onClick={switchToMessaging}
                    variant={serviceMode === 'messaging' ? 'contained' : 'outlined'}
                    sx={{
                      flex: 1,
                      background: serviceMode === 'messaging' 
                        ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' 
                        : 'transparent',
                      borderColor: isDarkMode ? '#7c8bf0' : '#667eea',
                      color: serviceMode === 'messaging' 
                        ? 'white' 
                        : (isDarkMode ? '#7c8bf0' : '#667eea')
                    }}
                  >
                    💬 TremSBCMsg
                  </Button>
                  <Button 
                    onClick={switchToEmail}
                    variant={serviceMode === 'email' ? 'contained' : 'outlined'}
                    sx={{
                      flex: 1,
                      background: serviceMode === 'email' 
                        ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' 
                        : 'transparent',
                      borderColor: isDarkMode ? '#7c8bf0' : '#667eea',
                      color: serviceMode === 'email' 
                        ? 'white' 
                        : (isDarkMode ? '#7c8bf0' : '#667eea')
                    }}
                  >
                    📧 TremSBCMail
                  </Button>
                  <Box
                    onClick={() => {
                      setMessageToAddress('');
                      setMessageContent('');
                      setEmailToAddress('');
                      setEmailSubject('');
                      setEmailBody('');
                      showNotification(serviceMode === 'messaging' ? '💬 Ready for new message' : '📧 Ready for new email', 'info');
                    }}
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      color: 'white',
                      fontSize: '18px',
                      fontWeight: 'bold',
                      boxShadow: '0 3px 8px rgba(102, 126, 234, 0.3)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'scale(1.05)',
                        boxShadow: '0 4px 10px rgba(102, 126, 234, 0.4)',
                      }
                    }}
                  >
                    +
                  </Box>
                </Box>
                <Typography variant="body2" sx={{ 
                  color: isDarkMode ? '#b0b6c3' : '#6c757d',
                  textAlign: 'center'
                }}>
                  {serviceMode === 'messaging' ? 'Messaging Mode' : 'Email Mode'}
                </Typography>
              </CardContent>
            </Card>
            
            {/* Mobile Communication Card */}
            <Card sx={{ 
              mb: 4, 
              background: isDarkMode 
                ? 'rgba(30, 30, 63, 0.7)' 
                : 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(20px)',
              border: isDarkMode 
                ? '1px solid rgba(255, 255, 255, 0.1)' 
                : '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: 3,
              boxShadow: isDarkMode 
                ? '0 10px 30px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.1)' 
                : '0 10px 30px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.3)',
              position: 'relative',
              zIndex: 2,
            }}>
              <CardContent>
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  mb: 3
                }}>
                  <Typography variant="h5" sx={{ 
                    color: isDarkMode ? '#7c8bf0' : '#667eea', 
                    fontWeight: 600 
                  }}>
                    {serviceMode === 'messaging' ? '💬 TremSBCMsg' : '📧 TremSBCMail'}
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={startNewConversation}
                    sx={{ 
                      borderColor: isDarkMode ? '#7c8bf0' : '#667eea',
                      color: isDarkMode ? '#7c8bf0' : '#667eea',
                      minWidth: 'auto',
                      px: 2,
                      py: 0.5,
                      fontSize: '0.75rem',
                      '&:hover': { 
                        borderColor: isDarkMode ? '#9ca3f0' : '#7c8bf0',
                        backgroundColor: isDarkMode ? 'rgba(124, 139, 240, 0.1)' : 'rgba(102, 126, 234, 0.1)'
                      }
                    }}
                  >
                    + New {serviceMode === 'messaging' ? 'Message' : 'Email'}
                  </Button>
                </Box>
                
                {serviceMode === 'messaging' ? (
                  // Mobile Messaging Interface
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="To Address"
                        placeholder="Enter recipient wallet address"
                        value={messageToAddress}
                        onChange={(e) => setMessageToAddress(e.target.value)}
                        sx={{ 
                          '& .MuiInputLabel-root': { 
                            color: isDarkMode ? '#b0b6c3' : '#6c757d'
                          },
                          '& .MuiOutlinedInput-root': { 
                            color: isDarkMode ? '#e0e6ed' : '#495057',
                            background: isDarkMode 
                              ? 'rgba(255, 255, 255, 0.05)' 
                              : 'rgba(255, 255, 255, 0.8)',
                            backdropFilter: 'blur(10px)',
                            '& fieldset': { 
                              borderColor: isDarkMode ? '#3a3a6e' : '#ced4da'
                            },
                            '&:hover fieldset': { 
                              borderColor: isDarkMode ? '#7c8bf0' : '#667eea'
                            },
                            '&.Mui-focused fieldset': { 
                              borderColor: isDarkMode ? '#7c8bf0' : '#667eea'
                            }
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel sx={{ 
                          color: isDarkMode ? '#b0b6c3' : '#6c757d'
                        }}>Message Type</InputLabel>
                        <Select
                          value={messageType}
                          onChange={(e) => setMessageType(e.target.value)}
                          sx={{ 
                            color: isDarkMode ? '#e0e6ed' : '#495057',
                            background: isDarkMode 
                              ? 'rgba(255, 255, 255, 0.05)' 
                              : 'rgba(255, 255, 255, 0.8)',
                            backdropFilter: 'blur(10px)',
                            '& .MuiOutlinedInput-notchedOutline': { 
                              borderColor: isDarkMode ? '#3a3a6e' : '#ced4da'
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': { 
                              borderColor: isDarkMode ? '#7c8bf0' : '#667eea'
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { 
                              borderColor: isDarkMode ? '#7c8bf0' : '#667eea'
                            }
                          }}
                        >
                          <MenuItem value="text">Text</MenuItem>
                          <MenuItem value="urgent">Urgent</MenuItem>
                          <MenuItem value="private">Private</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Message"
                        placeholder="Enter your TremSBCMsg message"
                        value={messageContent}
                        onChange={(e) => setMessageContent(e.target.value)}
                        sx={{ 
                          '& .MuiInputLabel-root': { 
                            color: isDarkMode ? '#b0b6c3' : '#6c757d'
                          },
                          '& .MuiOutlinedInput-root': { 
                            color: isDarkMode ? '#e0e6ed' : '#495057',
                            background: isDarkMode 
                              ? 'rgba(255, 255, 255, 0.05)' 
                              : 'rgba(255, 255, 255, 0.8)',
                            backdropFilter: 'blur(10px)',
                            '& fieldset': { 
                              borderColor: isDarkMode ? '#3a3a6e' : '#ced4da'
                            },
                            '&:hover fieldset': { 
                              borderColor: isDarkMode ? '#7c8bf0' : '#667eea'
                            },
                            '&.Mui-focused fieldset': { 
                              borderColor: isDarkMode ? '#7c8bf0' : '#667eea'
                            }
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      {/* Auto-mining Toggle - Mobile Messaging */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                        <Typography variant="body2" sx={{ 
                          color: isDarkMode ? '#e0e6ed' : '#495057',
                          fontWeight: 500 
                        }}>
                          Auto-mine:
                        </Typography>
                        <Box
                          onClick={toggleAutoMine}
                          sx={{
                            width: 48,
                            height: 24,
                            borderRadius: '12px',
                            background: autoMineEnabled 
                              ? 'linear-gradient(135deg, #28a745 0%, #20c997 100%)' 
                              : 'linear-gradient(135deg, #6c757d 0%, #adb5bd 100%)',
                            cursor: 'pointer',
                            position: 'relative',
                            boxShadow: autoMineEnabled 
                              ? '0 2px 8px rgba(40, 167, 69, 0.3)' 
                              : '0 2px 8px rgba(108, 117, 125, 0.2)',
                            transition: 'all 0.3s ease',
                          }}
                        >
                          <Box
                            sx={{
                              width: 18,
                              height: 18,
                              borderRadius: '50%',
                              background: 'white',
                              position: 'absolute',
                              top: '3px',
                              left: autoMineEnabled ? '27px' : '3px',
                              transition: 'all 0.3s ease',
                              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                            }}
                          />
                        </Box>
                      </Box>
                      {/* Manual Mine Button Mobile (when auto-mining is off) */}
                      {!autoMineEnabled && (
                        <Button
                          variant="outlined"
                          onClick={mineBlock}
                          fullWidth
                          sx={{ 
                            borderColor: isDarkMode ? '#7c8bf0' : '#667eea',
                            color: isDarkMode ? '#7c8bf0' : '#667eea',
                            mb: 2,
                            '&:hover': { 
                              borderColor: isDarkMode ? '#9ca3f0' : '#7c8bf0',
                              backgroundColor: isDarkMode ? 'rgba(124, 139, 240, 0.1)' : 'rgba(102, 126, 234, 0.1)'
                            }
                          }}
                        >
                          ⛏️ Mine Block
                        </Button>
                      )}
                      <Button
                        variant="contained"
                        startIcon={<SendIcon />}
                        onClick={sendMessage}
                        disabled={loadingState.message}
                        fullWidth
                        sx={{ 
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          color: 'white',
                          '&:hover': { 
                            opacity: 0.9,
                            transform: 'translateY(-1px)',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                          }
                        }}
                      >
                        {loadingState.message ? 'Sending...' : 'Send TremSBCMsg'}
                      </Button>
                    </Grid>
                  </Grid>
                ) : (
                  // Mobile Email Interface
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="To Address"
                        placeholder="Enter recipient wallet address"
                        value={emailToAddress}
                        onChange={(e) => setEmailToAddress(e.target.value)}
                        sx={{ 
                          '& .MuiInputLabel-root': { 
                            color: isDarkMode ? '#b0b6c3' : '#6c757d'
                          },
                          '& .MuiOutlinedInput-root': { 
                            color: isDarkMode ? '#e0e6ed' : '#495057',
                            background: isDarkMode 
                              ? 'rgba(255, 255, 255, 0.05)' 
                              : 'rgba(255, 255, 255, 0.8)',
                            backdropFilter: 'blur(10px)',
                            '& fieldset': { 
                              borderColor: isDarkMode ? '#3a3a6e' : '#ced4da'
                            },
                            '&:hover fieldset': { 
                              borderColor: isDarkMode ? '#7c8bf0' : '#667eea'
                            },
                            '&.Mui-focused fieldset': { 
                              borderColor: isDarkMode ? '#7c8bf0' : '#667eea'
                            }
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel sx={{ 
                          color: isDarkMode ? '#b0b6c3' : '#6c757d'
                        }}>Priority</InputLabel>
                        <Select
                          value={emailPriority}
                          onChange={(e) => setEmailPriority(e.target.value)}
                          sx={{ 
                            color: isDarkMode ? '#e0e6ed' : '#495057',
                            background: isDarkMode 
                              ? 'rgba(255, 255, 255, 0.05)' 
                              : 'rgba(255, 255, 255, 0.8)',
                            backdropFilter: 'blur(10px)',
                            '& .MuiOutlinedInput-notchedOutline': { 
                              borderColor: isDarkMode ? '#3a3a6e' : '#ced4da'
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': { 
                              borderColor: isDarkMode ? '#7c8bf0' : '#667eea'
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { 
                              borderColor: isDarkMode ? '#7c8bf0' : '#667eea'
                            }
                          }}
                        >
                          <MenuItem value="normal">Normal Priority</MenuItem>
                          <MenuItem value="high">High Priority</MenuItem>
                          <MenuItem value="low">Low Priority</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Subject"
                        placeholder="Subject"
                        value={emailSubject}
                        onChange={(e) => setEmailSubject(e.target.value)}
                        sx={{ 
                          '& .MuiInputLabel-root': { 
                            color: isDarkMode ? '#b0b6c3' : '#6c757d'
                          },
                          '& .MuiOutlinedInput-root': { 
                            color: isDarkMode ? '#e0e6ed' : '#495057',
                            background: isDarkMode 
                              ? 'rgba(255, 255, 255, 0.05)' 
                              : 'rgba(255, 255, 255, 0.8)',
                            backdropFilter: 'blur(10px)',
                            '& fieldset': { 
                              borderColor: isDarkMode ? '#3a3a6e' : '#ced4da'
                            },
                            '&:hover fieldset': { 
                              borderColor: isDarkMode ? '#7c8bf0' : '#667eea'
                            },
                            '&.Mui-focused fieldset': { 
                              borderColor: isDarkMode ? '#7c8bf0' : '#667eea'
                            }
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={4}
                        label="Message"
                        placeholder="Enter your TremSBCMail message"
                        value={emailBody}
                        onChange={(e) => setEmailBody(e.target.value)}
                        sx={{ 
                          '& .MuiInputLabel-root': { 
                            color: isDarkMode ? '#b0b6c3' : '#6c757d'
                          },
                          '& .MuiOutlinedInput-root': { 
                            color: isDarkMode ? '#e0e6ed' : '#495057',
                            background: isDarkMode 
                              ? 'rgba(255, 255, 255, 0.05)' 
                              : 'rgba(255, 255, 255, 0.8)',
                            backdropFilter: 'blur(10px)',
                            '& fieldset': { 
                              borderColor: isDarkMode ? '#3a3a6e' : '#ced4da'
                            },
                            '&:hover fieldset': { 
                              borderColor: isDarkMode ? '#7c8bf0' : '#667eea'
                            },
                            '&.Mui-focused fieldset': { 
                              borderColor: isDarkMode ? '#7c8bf0' : '#667eea'
                            }
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      {/* Auto-mining Toggle - Mobile Email */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                        <Typography variant="body2" sx={{ 
                          color: isDarkMode ? '#e0e6ed' : '#495057',
                          fontWeight: 500 
                        }}>
                          Auto-mine:
                        </Typography>
                        <Box
                          onClick={toggleAutoMine}
                          sx={{
                            width: 48,
                            height: 24,
                            borderRadius: '12px',
                            background: autoMineEnabled 
                              ? 'linear-gradient(135deg, #28a745 0%, #20c997 100%)' 
                              : 'linear-gradient(135deg, #6c757d 0%, #adb5bd 100%)',
                            cursor: 'pointer',
                            position: 'relative',
                            boxShadow: autoMineEnabled 
                              ? '0 2px 8px rgba(40, 167, 69, 0.3)' 
                              : '0 2px 8px rgba(108, 117, 125, 0.2)',
                            transition: 'all 0.3s ease',
                          }}
                        >
                          <Box
                            sx={{
                              width: 18,
                              height: 18,
                              borderRadius: '50%',
                              background: 'white',
                              position: 'absolute',
                              top: '3px',
                              left: autoMineEnabled ? '27px' : '3px',
                              transition: 'all 0.3s ease',
                              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                            }}
                          />
                        </Box>
                      </Box>
                      {/* Manual Mine Button Mobile (when auto-mining is off) */}
                      {!autoMineEnabled && (
                        <Button
                          variant="outlined"
                          onClick={mineBlock}
                          fullWidth
                          sx={{ 
                            borderColor: isDarkMode ? '#7c8bf0' : '#667eea',
                            color: isDarkMode ? '#7c8bf0' : '#667eea',
                            mb: 2,
                            '&:hover': { 
                              borderColor: isDarkMode ? '#9ca3f0' : '#7c8bf0',
                              backgroundColor: isDarkMode ? 'rgba(124, 139, 240, 0.1)' : 'rgba(102, 126, 234, 0.1)'
                            }
                          }}
                        >
                          ⛏️ Mine Block
                        </Button>
                      )}
                      <Button
                        variant="contained"
                        startIcon={<EmailIcon />}
                        onClick={sendEmail}
                        disabled={loadingState.email}
                        fullWidth
                        sx={{ 
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          color: 'white',
                          '&:hover': { 
                            opacity: 0.9,
                            transform: 'translateY(-1px)',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                          }
                        }}
                      >
                        {loadingState.email ? 'Sending...' : 'Send TremSBCMail'}
                      </Button>
                    </Grid>
                  </Grid>
                )}
              </CardContent>
            </Card>
          </>
        )}


        {/* Recent Messages Display */}
        {messages.length > 0 && (
          <Card sx={{ 
            mb: 4, 
            background: isDarkMode ? '#1e1e3f' : 'white',
            borderRadius: 3,
            boxShadow: isDarkMode ? '0 10px 30px rgba(0,0,0,0.3)' : '0 10px 30px rgba(0,0,0,0.1)',
          }}>
            <CardContent>
              <Typography variant="h6" sx={{ 
                color: isDarkMode ? '#7c8bf0' : '#667eea', 
                mb: 2 
              }}>Recent Messages</Typography>
              <List>
                {messages.slice(0, 5).map((message, index) => {
                  // BLOCKCHAIN SECURITY: Check confirmation status
                  const isConfirmed = message.status === 'confirmed' || message.confirmed || message.blockHeight;
                  const isOwn = message.from === walletAddress;
                  
                  return (
                    <ListItem key={index} sx={{
                      borderLeft: !isConfirmed ? '4px solid #ffa726' : isConfirmed ? '4px solid #4caf50' : 'none',
                      opacity: isConfirmed ? 1 : 0.7
                    }}>
                      <ListItemText
                        primary={isConfirmed 
                          ? message.content 
                          : (isOwn 
                              ? '⏳ Message pending blockchain confirmation...' 
                              : '🔐 Encrypted message - awaiting decryption...')}
                        secondary={`From: ${message.from.substring(0, 20)}... ${isConfirmed ? '✅ Confirmed' : '⏳ Pending'}`}
                        sx={{ 
                          '& .MuiListItemText-primary': { 
                            color: isDarkMode ? '#e0e6ed' : '#333',
                            fontStyle: !isConfirmed ? 'italic' : 'normal'
                          },
                          '& .MuiListItemText-secondary': { 
                            color: isDarkMode ? '#b0b6c3' : '#6c757d' 
                          }
                        }}
                      />
                    </ListItem>
                  );
                })}
              </List>
            </CardContent>
          </Card>
        )}

        {/* Recent Emails Display */}
        {emails.length > 0 && (
          <Card sx={{ 
            mb: 4, 
            background: isDarkMode ? '#1e1e3f' : 'white',
            borderRadius: 3,
            boxShadow: isDarkMode ? '0 10px 30px rgba(0,0,0,0.3)' : '0 10px 30px rgba(0,0,0,0.1)',
          }}>
            <CardContent>
              <Typography variant="h6" sx={{ 
                color: isDarkMode ? '#7c8bf0' : '#667eea', 
                mb: 2 
              }}>Recent Emails</Typography>
              <List>
                {emails.slice(0, 5).map((email, index) => {
                  // BLOCKCHAIN SECURITY: Check confirmation status for emails
                  const isConfirmed = email.status === 'confirmed' || email.confirmed || email.blockHeight;
                  const isOwn = email.from === walletAddress;
                  
                  return (
                    <ListItem key={index} sx={{
                      borderLeft: !isConfirmed ? '4px solid #ffa726' : isConfirmed ? '4px solid #4caf50' : 'none',
                      opacity: isConfirmed ? 1 : 0.7
                    }}>
                      <ListItemText
                        primary={isConfirmed 
                          ? (email.subject || email.data?.subject || 'No Subject') 
                          : (isOwn 
                              ? '⏳ Email pending blockchain confirmation...' 
                              : '🔐 Encrypted email - awaiting decryption...')}
                        secondary={`From: ${email.from.substring(0, 20)}... ${isConfirmed ? '✅ Confirmed' : '⏳ Pending'}`}
                        sx={{ 
                          '& .MuiListItemText-primary': { 
                            color: isDarkMode ? '#e0e6ed' : '#333',
                            fontStyle: !isConfirmed ? 'italic' : 'normal'
                          },
                          '& .MuiListItemText-secondary': { 
                            color: isDarkMode ? '#b0b6c3' : '#6c757d' 
                          }
                        }}
                      />
                    </ListItem>
                  );
                })}
              </List>
            </CardContent>
          </Card>
        )}
      </Container>

      {/* Scroll to Top FAB */}
      {showScrollTop && (
        <Fab
          color="primary"
          size="medium"
          onClick={scrollToTop}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            background: 'rgba(102, 126, 234, 0.9)',
            color: 'white',
            '&:hover': {
              background: 'rgba(102, 126, 234, 1)',
              transform: 'translateY(-2px)',
            },
          }}
        >
          <ArrowUpIcon />
        </Fab>
      )}

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.show}
        autoHideDuration={4000}
        onClose={() => setNotification({ ...notification, show: false })}
      >
        <Alert 
          severity={notification.type as any}
          onClose={() => setNotification({ ...notification, show: false })}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EnhancedTremChain;